import React, { useEffect, useState } from 'react';
import axios from 'axios';
import {
  Box,
  Flex,
  Text,
  Spinner,
  Input,
  HStack,
  Button,
  Heading,
  Image,
  SimpleGrid,
  useDisclosure,
  CloseButton,
  Dialog,
  Portal
} from '@chakra-ui/react';
import { LuPlus, LuPencil, LuTrash2, LuExternalLink } from 'react-icons/lu';
import { CONFIG_BASE_URL } from '../../core/constants';
import { COLORS } from '../../../theme';
import VideoDialog from './VideoDialog';
import ReorderVideosDialog from './ReorderVideosDialog';

export default function ManageVideos() {
  const [videos, setVideos] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [query, setQuery] = useState('');
  const [message, setMessage] = useState(null);

  const {
    open: isDialogOpen,
    onOpen: dialogOnOpen,
    onClose: dialogOnClose
  } = useDisclosure();

  const {
    open: isReorderOpen,
    onOpen: reorderOnOpen,
    onClose: reorderOnClose
  } = useDisclosure();

  const [dialogMode, setDialogMode] = useState('create');
  const [editingIndex, setEditingIndex] = useState(null);

  const {
    open: deleteIsOpen,
    onOpen: deleteOnOpen,
    onClose: deleteOnClose
  } = useDisclosure();
  const [deletingIndex, setDeletingIndex] = useState(null);

  useEffect(() => {
    loadVideos();
  }, []);

  async function loadVideos() {
    setLoading(true);
    try {
      const res = await axios.get(`${CONFIG_BASE_URL}/videos.json`, {
        headers: { Accept: 'application/vnd.github.v3.raw' }
      });
      setVideos(res.data || []);
      setMessage(null);
    } catch (err) {
      console.error(err);
      setMessage({ type: 'error', text: 'Failed to load videos' });
      setVideos([]);
    } finally {
      setLoading(false);
    }
  }

  const openCreate = () => {
    setDialogMode('create');
    setEditingIndex(null);
    dialogOnOpen();
  };

  const openEdit = (index) => {
    setDialogMode('edit');
    setEditingIndex(index);
    dialogOnOpen();
  };

  const openDelete = (index) => {
    setDeletingIndex(index);
    deleteOnOpen();
  };

  const handleSave = async (videoData) => {
    setIsSaving(true);
    try {
      let updatedVideos = [...videos];
      if (dialogMode === 'create') {
        updatedVideos.push(videoData);
      } else {
        updatedVideos[editingIndex] = videoData;
      }

      await commitVideos(updatedVideos);
      setMessage({
        type: 'success',
        text: `Video ${dialogMode === 'create' ? 'added' : 'updated'} successfully`
      });
      dialogOnClose();
      setVideos(updatedVideos);
    } catch (err) {
      console.error(err);
      setMessage({ type: 'error', text: 'Failed to save: ' + err.message });
    } finally {
      setIsSaving(false);
    }
  };

  const handleDelete = async () => {
    setIsSaving(true);
    try {
      const updatedVideos = videos.filter((_, idx) => idx !== deletingIndex);
      await commitVideos(updatedVideos);
      setMessage({ type: 'success', text: 'Video deleted' });
      setVideos(updatedVideos);
      deleteOnClose();
    } catch (err) {
      console.error(err);
      setMessage({ type: 'error', text: 'Delete failed: ' + err.message });
    } finally {
      setIsSaving(false);
    }
  };

  const handleReorderSave = async (updatedVideos) => {
    setIsSaving(true);
    try {
      await commitVideos(updatedVideos);
      setVideos(updatedVideos);
      setMessage({ type: 'success', text: 'Videos reordered successfully' });
      reorderOnClose();
    } catch (err) {
      console.error(err);
      setMessage({ type: 'error', text: 'Failed to reorder: ' + err.message });
    } finally {
      setIsSaving(false);
    }
  };

  async function commitVideos(newVideos) {
    await axios.post(
      '/api/github/update-config',
      {
        type: 'video',
        content: JSON.stringify(newVideos, null, 2)
      },
      { headers: { 'Content-Type': 'application/json' } }
    );
  }

  const filteredVideos = videos?.filter((video) => {
    if (!query) return true;
    const searchString = `${video.title} ${video.description}`.toLowerCase();
    return searchString.includes(query.toLowerCase());
  });

  if (loading) {
    return (
      <Box p={8} textAlign="center">
        <Spinner size="xl" color={COLORS.brand.primary} />
        <Text mt={4} color="gray.500">
          Loading videos...
        </Text>
      </Box>
    );
  }

  return (
    <Box maxW="1200px" mx="auto" p={4}>
      <Flex justify="space-between" align="center" mb={6}>
        <Box>
          <Heading size="lg">Manage Videos</Heading>
          <Text color="gray.500" fontSize="sm">
            Update your video gallery content
          </Text>
        </Box>
      </Flex>

      <Box mb={6}>
        <HStack gap={3}>
          <Input
            placeholder="Search videos..."
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            maxW="400px"
            borderRadius="full"
            bg="white"
            _dark={{ bg: 'gray.800' }}
          />

          <Button
            variant="outline"
            onClick={reorderOnOpen}
            disabled={!videos || videos.length < 2}
          >
            Reorder Videos
          </Button>
          <Button
            colorPalette="orange"
            bg={COLORS.brand.primary}
            _hover={{ bg: COLORS.brand.hover }}
            onClick={openCreate}
            leftIcon={<LuPlus />}
          >
            Add New Video
          </Button>
        </HStack>
      </Box>

      {message && (
        <Box
          mb={6}
          p={4}
          borderRadius="md"
          bg={message.type === 'error' ? 'red.50' : 'blue.50'}
          color={message.type === 'error' ? 'red.700' : 'blue.700'}
          borderWidth="1px"
          borderColor={message.type === 'error' ? 'red.200' : 'blue.200'}
        >
          {message.text}
        </Box>
      )}

      {filteredVideos.length === 0 ? (
        <Box
          p={10}
          textAlign="center"
          borderWidth="1px"
          borderStyle="dashed"
          borderRadius="lg"
        >
          <Text color="gray.500">
            No videos found. Click "Add New Video" to get started.
          </Text>
        </Box>
      ) : (
        <SimpleGrid columns={{ base: 1, md: 2, lg: 3 }} gap={6}>
          {filteredVideos.map((video, idx) => {
            const originalIndex = videos.indexOf(video);
            return (
              <Box
                key={idx}
                borderWidth="1px"
                borderRadius="lg"
                overflow="hidden"
                bg="white"
                _dark={{ bg: 'gray.800', borderColor: 'gray.700' }}
                boxShadow="sm"
                transition="transform 0.2s"
                _hover={{ transform: 'translateY(-4px)', boxShadow: 'md' }}
              >
                <Box position="relative" h="200px">
                  <Image
                    src={`https://img.youtube.com/vi/${video.youtubeId}/maxresdefault.jpg`}
                    alt={video.title}
                    fallbackSrc={`https://img.youtube.com/vi/${video.youtubeId}/mqdefault.jpg`}
                    w="full"
                    h="full"
                    objectFit="cover"
                  />
                  <Box
                    position="absolute"
                    top={0}
                    left={0}
                    w="full"
                    h="full"
                    bg="blackAlpha.300"
                    opacity={0}
                    _hover={{ opacity: 1 }}
                    transition="opacity 0.2s"
                    display="flex"
                    alignItems="center"
                    justifyContent="center"
                  >
                    <Button
                      as="a"
                      href={`https://youtube.com/watch?v=${video.youtubeId}`}
                      target="_blank"
                      size="sm"
                      bg="white"
                      color="black"
                      _hover={{ bg: 'gray.100' }}
                    >
                      <LuExternalLink /> Watch on YouTube
                    </Button>
                  </Box>
                </Box>

                <Box p={4}>
                  <Heading size="md" mb={2} noOfLines={1}>
                    {video.title}
                  </Heading>
                  <Text
                    fontSize="sm"
                    color="gray.600"
                    _dark={{ color: 'gray.400' }}
                    mb={4}
                    h="40px"
                    noOfLines={2}
                  >
                    {video.description || 'No description provided.'}
                  </Text>

                  <Flex justify="flex-end" gap={2}>
                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={() => openEdit(originalIndex)}
                      leftIcon={<LuPencil />}
                    >
                      Edit
                    </Button>
                    <Button
                      size="sm"
                      variant="ghost"
                      colorPalette="red"
                      onClick={() => openDelete(originalIndex)}
                      leftIcon={<LuTrash2 />}
                    >
                      Delete
                    </Button>
                  </Flex>
                </Box>
              </Box>
            );
          })}
        </SimpleGrid>
      )}

      {/* Dialogs */}
      <VideoDialog
        isOpen={isDialogOpen}
        onClose={dialogOnClose}
        mode={dialogMode}
        videoData={editingIndex !== null ? videos[editingIndex] : null}
        isSaving={isSaving}
        onSave={handleSave}
      />

      <ReorderVideosDialog
        isOpen={isReorderOpen}
        onClose={reorderOnClose}
        videos={videos || []}
        isSaving={isSaving}
        onSave={handleReorderSave}
      />

      <Dialog.Root
        placement="center"
        motionPreset="slide-in-bottom"
        open={deleteIsOpen}
        onOpenChange={(details) => !details.open && deleteOnClose()}
      >
        <Portal>
          <Dialog.Backdrop />
          <Dialog.Positioner>
            <Dialog.Content>
              <Dialog.Header>
                <Dialog.Title>Confirm Deletion</Dialog.Title>
                <Dialog.CloseTrigger asChild>
                  <CloseButton size="sm" />
                </Dialog.CloseTrigger>
              </Dialog.Header>
              <Dialog.Body>
                Are you sure you want to remove{' '}
                <strong>
                  {deletingIndex !== null && videos[deletingIndex]?.title}
                </strong>
                ? This event cannot be undone.
              </Dialog.Body>
              <Dialog.Footer>
                <Flex justify="flex-end" gap={2} w="full">
                  <Button onClick={deleteOnClose} variant="outline">
                    Cancel
                  </Button>
                  <Button
                    colorPalette="red"
                    onClick={handleDelete}
                    loading={isSaving}
                  >
                    Delete
                  </Button>
                </Flex>
              </Dialog.Footer>
            </Dialog.Content>
          </Dialog.Positioner>
        </Portal>
      </Dialog.Root>
    </Box>
  );
}
